import { useState } from 'react';
import { toast } from 'sonner';
import { addDoc, collection, Timestamp, doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase-config';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { UserSearchAutocomplete } from '../UserSearchAutocomplete';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';

interface User {
  id: string;
  email: string;
  name?: string;
  customerId?: string;
  createdAt: Date;
  createdVia?: 'self' | 'admin_proxy';
  pendingVerification?: boolean;
}

interface SubmitOnBehalfModalProps {
  customerId: string;
  brandColor: string;
  onClose: () => void;
  onSuccess: () => void;
}

const createUserSchema = z.object({
  email: z.string().email('Invalid email address'),
  name: z.string().optional(),
});

const requestSchema = z.object({
  title: z.string().min(5, 'Title must be at least 5 characters'),
  description: z.string().min(20, 'Description must be at least 20 characters'),
  category: z.enum(['feature', 'improvement', 'bug_fix', 'other']),
  source: z.enum(['support_ticket', 'customer_call', 'email', 'other']).optional(),
  internalNote: z.string().optional(),
});

type CreateUserFormData = z.infer<typeof createUserSchema>;
type RequestFormData = z.infer<typeof requestSchema>;

type Step = 'select_user' | 'create_user' | 'request_form';

export default function SubmitOnBehalfModal({
  customerId,
  brandColor,
  onClose,
  onSuccess,
}: SubmitOnBehalfModalProps) {
  const { user, userProfile } = useAuth();
  const [step, setStep] = useState<Step>('select_user');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register: registerUser,
    handleSubmit: handleSubmitUser,
    formState: { errors: userErrors },
    setValue: setUserValue,
  } = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
  });

  const {
    register: registerRequest,
    handleSubmit: handleSubmitRequest,
    formState: { errors: requestErrors },
  } = useForm<RequestFormData>({
    resolver: zodResolver(requestSchema),
    defaultValues: {
      category: 'feature',
      source: 'support_ticket',
    },
  });

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setStep('request_form');
  };

  const handleCreateNewUser = (email: string) => {
    setUserValue('email', email);
    setStep('create_user');
  };

  const onCreateUser = async (data: CreateUserFormData) => {
    if (!user || !userProfile) {
      setError('You must be logged in');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Create new user document
      const newUserId = `${customerId}_${Date.now()}`;
      const newUserRef = doc(db, 'users', newUserId);

      const newUserData = {
        id: newUserId,
        email: data.email.toLowerCase(),
        name: data.name || '',
        customerId: customerId,
        createdAt: Timestamp.now(),
        createdVia: 'admin_proxy' as const,
        pendingVerification: true,
      };

      await setDoc(newUserRef, newUserData);

      const createdUser: User = {
        id: newUserId,
        email: data.email.toLowerCase(),
        name: data.name || '',
        customerId: customerId,
        createdAt: new Date(),
        createdVia: 'admin_proxy',
        pendingVerification: true,
      };

      setSelectedUser(createdUser);
      setStep('request_form');
    } catch (err) {
      console.error('Error creating user:', err);
      setError('Failed to create user. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmitRequest = async (data: RequestFormData) => {
    if (!user || !userProfile || !selectedUser) {
      setError('Missing required information');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Create request document with submittedBy metadata
      const requestDoc = await addDoc(collection(db, 'customers', customerId, 'requests'), {
        title: data.title,
        description: data.description,
        category: data.category,
        customerId: customerId,
        createdBy: selectedUser.id,
        createdByEmail: selectedUser.email,
        createdByNickname: selectedUser.name || selectedUser.email.split('@')[0],
        status: 'open',
        voteCount: 1, // Creator automatically votes
        submittedBy: {
          adminId: user.uid,
          adminName: userProfile.nickname || user.email,
          onBehalfOf: selectedUser.id,
          source: data.source || 'other',
          internalNote: data.internalNote || '',
          timestamp: Timestamp.now(),
        },
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      // Create initial vote from the user on whose behalf this is being submitted
      await addDoc(
        collection(db, 'customers', customerId, 'requests', requestDoc.id, 'votes'),
        {
          userId: selectedUser.id,
          userEmail: selectedUser.email,
          userNickname: selectedUser.name || '',
          customerId: customerId,
          requestId: requestDoc.id,
          votedBy: {
            adminId: user.uid,
            adminName: userProfile.nickname || user.email,
            onBehalfOf: selectedUser.id,
            timestamp: Timestamp.now(),
          },
          createdAt: Timestamp.now(),
        }
      );

      // TODO: Send email notification to user
      console.log('Request submitted on behalf of:', selectedUser.email);
      console.log('Request ID:', requestDoc.id);

      setSuccess(true);
      toast.success(`Request successfully submitted on behalf of ${selectedUser.name || selectedUser.email}`);

      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error submitting request:', err);
      setError('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step === 'create_user') {
      setStep('select_user');
    } else if (step === 'request_form') {
      setStep('select_user');
      setSelectedUser(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            {step !== 'select_user' && (
              <button
                onClick={handleBack}
                className="text-muted-foreground hover:text-foreground transition-colors"
                disabled={isSubmitting || success}
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div>
              <h2 className="text-2xl font-semibold text-foreground">
                {step === 'select_user' && 'Submit Request on Behalf'}
                {step === 'create_user' && 'Create New User'}
                {step === 'request_form' && 'Submit Feature Request'}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {step === 'select_user' && 'Select or create a user'}
                {step === 'create_user' && 'User will receive welcome email'}
                {step === 'request_form' && `Submitting for ${selectedUser?.email}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            disabled={isSubmitting}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Select User */}
          {step === 'select_user' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Who is this request for?</Label>
                <UserSearchAutocomplete
                  customerId={customerId}
                  onUserSelect={handleUserSelect}
                  onCreateNew={handleCreateNewUser}
                  placeholder="Search by email..."
                />
              </div>
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <p className="text-sm text-blue-600 dark:text-blue-400">
                  💡 Search for an existing user or create a new one. The user will be
                  notified via email.
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Create User */}
          {step === 'create_user' && (
            <form onSubmit={handleSubmitUser(onCreateUser)} className="space-y-6">
              {error && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">
                  Email Address <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  {...registerUser('email')}
                  className={userErrors.email ? 'border-destructive' : ''}
                  disabled={isSubmitting}
                />
                {userErrors.email && (
                  <p className="text-sm text-destructive">{userErrors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Name (Optional)</Label>
                <Input
                  id="name"
                  type="text"
                  {...registerUser('name')}
                  placeholder="John Doe"
                  disabled={isSubmitting}
                />
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <p className="text-sm text-blue-600 dark:text-blue-400">
                  ℹ️ This user will receive a welcome email and can sign in later using
                  magic link authentication.
                </p>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <Button type="button" variant="outline" onClick={handleBack}>
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  style={{ backgroundColor: brandColor }}
                  className="text-white"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create User'
                  )}
                </Button>
              </div>
            </form>
          )}

          {/* Step 3: Request Form */}
          {step === 'request_form' && (
            <form onSubmit={handleSubmitRequest(onSubmitRequest)} className="space-y-6">
              {success && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-medium">
                      Request submitted on behalf of {selectedUser?.email}
                    </span>
                  </div>
                  <p className="text-sm mt-1 text-green-600/80 dark:text-green-400/80">
                    User will receive an email notification.
                  </p>
                </div>
              )}

              {error && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Submitting for:</p>
                    <p className="text-sm text-muted-foreground">{selectedUser?.email}</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setStep('select_user')}
                    disabled={isSubmitting || success}
                  >
                    Change
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">
                  Request Title <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="title"
                  {...registerRequest('title')}
                  placeholder="Brief description of the request"
                  className={requestErrors.title ? 'border-destructive' : ''}
                  disabled={isSubmitting || success}
                />
                {requestErrors.title && (
                  <p className="text-sm text-destructive">{requestErrors.title.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">
                  Description <span className="text-destructive">*</span>
                </Label>
                <textarea
                  id="description"
                  {...registerRequest('description')}
                  rows={5}
                  placeholder="Provide details about the request..."
                  className={`w-full px-3 py-2 rounded-md border bg-background text-foreground resize-none ${
                    requestErrors.description ? 'border-destructive' : 'border-input'
                  }`}
                  disabled={isSubmitting || success}
                />
                {requestErrors.description && (
                  <p className="text-sm text-destructive">
                    {requestErrors.description.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">
                  Category <span className="text-destructive">*</span>
                </Label>
                <select
                  id="category"
                  {...registerRequest('category')}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-foreground"
                  disabled={isSubmitting || success}
                >
                  <option value="feature">New Feature</option>
                  <option value="improvement">Improvement</option>
                  <option value="bug_fix">Bug Fix</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="border-t border-border pt-6 mt-6">
                <h3 className="text-sm font-medium text-foreground mb-4">
                  Admin Only Section
                </h3>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="source">Source</Label>
                    <select
                      id="source"
                      {...registerRequest('source')}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-foreground"
                      disabled={isSubmitting || success}
                    >
                      <option value="support_ticket">Support Ticket</option>
                      <option value="customer_call">Customer Call</option>
                      <option value="email">Email</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="internalNote">
                      Internal Note (not visible to user)
                    </Label>
                    <textarea
                      id="internalNote"
                      {...registerRequest('internalNote')}
                      rows={3}
                      placeholder="e.g., From ticket #5678, customer mentioned this on call..."
                      className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground resize-none"
                      disabled={isSubmitting || success}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <p className="text-sm text-blue-600 dark:text-blue-400">
                  ✉️ User will receive an email notification about this request.
                </p>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || success}
                  style={{ backgroundColor: brandColor }}
                  className="text-white"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : success ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Submitted!
                    </>
                  ) : (
                    'Submit Request'
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
